/**
 * Xpert API Client
 *
 * Centralised axios instance with two interceptors:
 *
 *   1. Request — attaches the current access token from authStorage.
 *
 *   2. Response — on 401, transparently calls /api/auth/refresh and retries
 *      the failed request. Concurrent 401s share a single in-flight refresh
 *      via `pendingQueue` so we never trigger the backend's refresh-reuse
 *      detection (W3-5), which would otherwise revoke all sessions when N
 *      simultaneous tab requests each tried to refresh independently.
 *
 *      Mirrors the mobile pattern in
 *      Expertbase_MobileApp/frontend/utils/api.ts. P0-4 fix.
 */
import axios from "axios";
import {
  getUserToken,
  setUserToken,
  getRefreshToken,
  setRefreshToken,
  clearAllUserState,
} from "../lib/authStorage";

// Dev: relative path so the CRA dev server's setupProxy.js handles the
// upstream call server-side (no browser CORS). Prod: REACT_APP_BACKEND_URL.
const IS_DEV = process.env.NODE_ENV === "development";
const BACKEND_URL = IS_DEV
  ? ""
  : (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
export const API_BASE_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

// ─── Request interceptor — attach token ──────────────────────────────────────

api.interceptors.request.use(
  (config) => {
    const token = getUserToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response interceptor — refresh-on-401 with concurrency queue ────────────

let isRefreshing = false;
let pendingQueue = [];

function _processQueue(error, token) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  pendingQueue = [];
}

async function _doRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("no refresh token");
  }
  // Use a bare axios call to avoid recursion into this interceptor.
  const r = await axios.post(`${API_BASE_URL}/auth/refresh`, {
    refresh_token: refreshToken,
  });
  const { token, refresh_token: newRefreshToken } = r.data;
  setUserToken(token);
  if (newRefreshToken) setRefreshToken(newRefreshToken);
  return token;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    // Only attempt refresh once per request, and not for the refresh call itself.
    const isAuthEndpoint =
      originalRequest.url && originalRequest.url.includes("/auth/refresh");

    if (status !== 401 || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If a refresh is in flight, queue this request behind it.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    try {
      const newToken = await _doRefresh();
      _processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      _processQueue(refreshError, null);
      // Refresh failed → wipe everything (mirrors mobile doLogout).
      clearAllUserState();
      // Redirect to login unless already on a public auth page.
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        if (!path.includes("/login") && !path.includes("/register") &&
            !path.includes("/forgot-password")) {
          window.location.href = "/login?reason=expired";
        }
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const setAuthToken = (token) => {
  setUserToken(token);
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

export const getErrorMessage = (error) => {
  if (error.response?.data?.detail) return error.response.data.detail;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.message) return error.message;
  return "An unexpected error occurred";
};

// Exposed for tests
export const __test = { _processQueue, _doRefresh };
